import { unwrapResult } from "@reduxjs/toolkit";
import * as cookie from "cookie";

import { createNoClassroomChangeToast } from "components/shared/toasts";
import { FORUM_URL, TLD } from "config";
import { AppDispatch } from "pubsub/client";
import {
  featureBreakoutRoomResource,
  setAllStagePanes,
  setCurrentFilter,
  setParticipantBarUserIds,
} from "shared/reducers/classroom";
import {
  BreakoutRoomType,
  PageUnloadReasonType,
  PaneType,
  PreclassGroupType,
  ResourceType,
} from "shared/types";
import { UserType } from "shared/types/user";
import { FilterStrategyType, pickUsers } from "shared/utils/user_picker";
import {
  EMPTY_PANE,
  buildStagePanesForFeaturingGroup,
  buildStagePanesWithUsers,
} from "utils/stage";

function _getDisplayMessage(reason: PageUnloadReasonType | undefined) {
  switch (reason) {
    case PageUnloadReasonType.CLASS_NOT_IN_FLIGHT:
      return "The class is not ready. If you have not tried yet, please use the Enter Class button to enter.";
    case PageUnloadReasonType.CLASS_NOT_IN_FLIGHT_FOR_MONITORING:
      return "Something went wrong. The class might not be a Flight class, the URL might be incorrect, or the class has never been loaded onto our servers and cannot be monitored yet.";
    case PageUnloadReasonType.KICKED_OUT_OF_CLASS_INACTIVE_USER:
      return "You have been kicked out of class for being inactive after class ended.";
    case PageUnloadReasonType.KICKED_OUT_OF_CLASS_NEW_TAB:
      return "You entered class in another tab or browser.";
    case PageUnloadReasonType.LOGGED_INTO_WRONG_ORG:
      return "You have been kicked out of class because you logged into a different organization.";
    case PageUnloadReasonType.NOT_PERMITTED_TO_MONITOR_CLASS:
      return "You are not permitted to access this page.";
    case PageUnloadReasonType.TOO_EARLY_TO_ENTER_CLASSROOM:
      return "You are not allowed to enter class until 1 hour before its start time.";
    case PageUnloadReasonType.KICKED_OUT_OF_CLASS_SERVER_REASSIGNMENT:
      return "Something went wrong. Please use the Enter Class button to try reentering.";
    default:
      return "You are not permitted in class.";
  }
}

function _addRedirectFromFlightCookie(prefix: string, reason: PageUnloadReasonType | undefined) {
  document.cookie = cookie.serialize(
    "redirect-from-flight",
    `${prefix}:${_getDisplayMessage(reason)}`,
    {
      maxAge: 5 * 60,
      path: "/",
      sameSite: "lax",
      secure: true,
      domain: `.${TLD}`,
    },
  );
}

export function getClassDetailPath({
  courseId,
  sectionId,
  classId,
}: {
  courseId?: string;
  sectionId?: string;
  classId: string;
}) {
  return `courses/${courseId || "0"}/sections/${sectionId || "0"}/classes/${classId}`;
}

export function redirectToForum(
  path: string,
  reason: PageUnloadReasonType | undefined = undefined,
  addAppPrefix = true,
) {
  window.flightLogger?.warn("Redirecting to Forum", reason);
  window.pageUnloadReason = reason || PageUnloadReasonType.KICKED_OUT_OF_CLASS;
  const fullPath = addAppPrefix ? `${FORUM_URL}/app/${path}` : `${FORUM_URL}/${path}`;
  window.location.href = fullPath;
}

export function redirectToClassEditPage(
  {
    courseId,
    sectionId,
    classId,
  }: {
    courseId?: string;
    sectionId?: string;
    classId: string;
  },
  reason: PageUnloadReasonType | undefined,
) {
  _addRedirectFromFlightCookie(classId, reason);
  redirectToForum(getClassDetailPath({ courseId, sectionId, classId }), reason);
}

export function redirectToHome(
  reason: PageUnloadReasonType | undefined = PageUnloadReasonType.NOT_PERMITTED_TO_ENTER_CLASSROOM,
) {
  _addRedirectFromFlightCookie("home", reason);
  redirectToForum("", reason);
}

export function redirectToLogin() {
  const newPath = `${window.location.pathname}${window.location.search}`;
  const prefix = !window.location.href.includes("monitoring") ? "/app" : "";
  redirectToForum(`?next=${prefix}${newPath}`, PageUnloadReasonType.NOT_LOGGED_IN, false);
}

export function redirectToNotFound() {
  redirectToForum("not-found", PageUnloadReasonType.PAGE_NOT_FOUND);
}

export async function pickUsersAndSetParticipantBarAndStage(
  dispatch: AppDispatch,
  maxSubscribedStreams: number,
  currentUserId: string,
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
  participantBarUserIds: string[],
  currentPanes: PaneType[],
  strategy: FilterStrategyType | undefined = undefined,
  forTimeline = false,
) {
  dispatch(setCurrentFilter({ filterStrategy: strategy }));

  const [sortedUsersForParticipantBar, sortedUsersForStage] = pickUsers(
    presentVisibleUsers,
    isAlwaysSubscribedToUserIds,
    canParticipateInClassUserIds,
    currentPanes,
    strategy,
    participantBarUserIds,
    true,
    undefined,
    undefined,
    forTimeline,
  );

  if (strategy && sortedUsersForParticipantBar.length === 0) {
    if (!forTimeline) {
      createNoClassroomChangeToast(
        "Selected filter does not currently match any participants outside the discussion.",
      );
    }
    return [];
  }

  const updatedParticipantBarUserIds = sortedUsersForParticipantBar
    .slice(0, maxSubscribedStreams)
    .map((user) => user.id);
  const actionResult = await dispatch(
    setParticipantBarUserIds({
      userIds: updatedParticipantBarUserIds,
      leaderId: currentUserId,
      updateFilterCount: true,
    }),
  );

  if (forTimeline) {
    return updatedParticipantBarUserIds;
  }

  const result = unwrapResult(actionResult);
  if (!result.updated && presentVisibleUsers.length > maxSubscribedStreams) {
    createNoClassroomChangeToast(result.msg);
  }

  const [updatedStagePanes, changed] = buildStagePanesWithUsers(
    currentPanes,
    sortedUsersForStage
      .filter((user) => updatedParticipantBarUserIds.includes(user.id))
      .map((user) => user.id),
    presentVisibleUsers,
    isAlwaysSubscribedToUserIds,
  );

  if (changed) {
    window.flightLogger?.log(
      `[pickUsersAndSetParticipantBarAndStage] Updating stage panes: ${JSON.stringify(
        updatedStagePanes,
      )}`,
    );
    dispatch(
      setAllStagePanes({
        panes: updatedStagePanes,
        ownerId: currentUserId,
      }),
    );
  } else if (presentVisibleUsers.length <= maxSubscribedStreams) {
    createNoClassroomChangeToast(
      "Selected participants are already in all available panes on the stage.",
    );
  }

  return updatedParticipantBarUserIds;
}

export function featureGroup(
  dispatch: AppDispatch,
  breakoutRoom: BreakoutRoomType | undefined,
  preclassGroup: PreclassGroupType | undefined,
  resource: ResourceType | undefined,
  maxSubscribedStreams: number,
  currentUserId: string,
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
  participantBarUserIds: string[],
) {
  const [sortedUsersForParticipantBar, sortedUsersForStage] = pickUsers(
    presentVisibleUsers,
    isAlwaysSubscribedToUserIds,
    canParticipateInClassUserIds,
    [],
    breakoutRoom ? FilterStrategyType.BreakoutGroup : FilterStrategyType.PreclassGroup,
    participantBarUserIds,
    true,
    breakoutRoom,
    preclassGroup,
  );

  if (breakoutRoom) {
    dispatch(
      setCurrentFilter({
        filterStrategy: `breakout_group:${breakoutRoom.breakout_id}`,
      }),
    );
  }

  // If no one in the group is present, feature the resource or show an empty pane
  if (sortedUsersForParticipantBar.length === 0) {
    if (breakoutRoom && resource) {
      dispatch(featureBreakoutRoomResource({ breakoutRoomId: breakoutRoom.id }));
    } else {
      window.flightLogger?.log(`[featureGroup] Updating stage panes: empty 1-up`);
      dispatch(
        setAllStagePanes({
          panes: [EMPTY_PANE],
        }),
      );
    }
    return;
  }

  const updatedParticipantBarUserIds = sortedUsersForParticipantBar
    .slice(0, maxSubscribedStreams)
    .map((user) => user.id);
  dispatch(
    setParticipantBarUserIds({
      userIds: updatedParticipantBarUserIds,
      leaderId: currentUserId,
      updateFilterCount: true,
    }),
  );

  const updatedStagePanes = buildStagePanesForFeaturingGroup(
    sortedUsersForStage
      .filter((user) => updatedParticipantBarUserIds.includes(user.id))
      .map((user) => user.id),
    resource?.id,
  );
  window.flightLogger?.log(
    `[featureGroup] Updating stage panes: ${JSON.stringify(updatedStagePanes)}`,
  );
  dispatch(
    setAllStagePanes({
      panes: updatedStagePanes,
      ownerId: currentUserId,
    }),
  );
}

export function featureBreakoutRoom(
  dispatch: AppDispatch,
  breakoutRoom: BreakoutRoomType,
  breakoutNotesResource: ResourceType | undefined,
  maxSubscribedStreams: number,
  currentUserId: string,
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
  participantBarUserIds: string[],
) {
  featureGroup(
    dispatch,
    breakoutRoom,
    undefined,
    breakoutNotesResource,
    maxSubscribedStreams,
    currentUserId,
    presentVisibleUsers,
    isAlwaysSubscribedToUserIds,
    canParticipateInClassUserIds,
    participantBarUserIds,
  );
}

export function featurePreclassGroup(
  dispatch: AppDispatch,
  preclassGroup: PreclassGroupType,
  maxSubscribedStreams: number,
  currentUserId: string,
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
  participantBarUserIds: string[],
) {
  featureGroup(
    dispatch,
    undefined,
    preclassGroup,
    undefined,
    maxSubscribedStreams,
    currentUserId,
    presentVisibleUsers,
    isAlwaysSubscribedToUserIds,
    canParticipateInClassUserIds,
    participantBarUserIds,
  );
}
